import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Text } from 'components/ui';
import { mediaQueries } from 'style/config';
import Actions from 'Actions';
import localeUtils from 'utils/LanguageLocale';
import deliveryFrequencyUtils from 'utils/DeliveryFrequency';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import RwdProductPageEN from 'components/ProductPage/locales/RwdProductPage_en_US';
import RwdProductPageFR from 'components/ProductPage/locales/RwdProductPage_fr_CA';
import AutoReplenishmentEN from 'components/DeliveryInfo/AutoReplenishment/locales/AutoReplenishment_en_US';
import AutoReplenishmentFR from 'components/DeliveryInfo/AutoReplenishment/locales/AutoReplenishment_fr_CA';
import { isChangeQuantityEnabled } from 'utils/arChangeQuantityFlag';

const getText = localeUtils.getLocalisedDataFromResource(RwdProductPageEN, RwdProductPageFR);
const getAutoReplenText = localeUtils.getLocalisedDataFromResource(AutoReplenishmentEN, AutoReplenishmentFR);
const { formatSavingAmountString } = deliveryFrequencyUtils;

const useCommerce = () => {
    const dispatch = useDispatch();
    const currentProduct = useSelector(state => state.page.product, shallowEqual);
    const currentSku = currentProduct?.currentSku;

    const [quantity, setQuantity] = useState(1);
    const [autoReplenishChecked, setAutoReplenishChecked] = useState(false);
    const [replenishmentFreqNum, setReplenishmentFreqNum] = useState(currentSku?.replenishmentFreqNum || '');
    const [replenishmentFreqType, setReplenishmentFreqType] = useState(currentSku?.replenishmentFreqType || '');
    const [isFrequencyModalOpen, setIsFrequencyModalOpen] = useState(false);
    const [isAutoReplenMostCommon, setIsAutoReplenMostCommon] = useState(false);

    const prevSkuIdRef = useRef(currentSku?.skuId);
    const prevProductIdRef = useRef(currentProduct?.productDetails?.productId);
    const prevAutoReplenishRef = useRef(autoReplenishChecked);

    const checkIsAutoReplenMostCommon = () => {
        return replenishmentFreqNum === currentSku?.replenishmentFreqNum && replenishmentFreqType === currentSku?.replenishmentFreqType;
    };

    const fireDeliveryFrequencyModalTracking = isOpen => {
        (isOpen ? pdpAnalytics.trackFrequencyModalOpen : pdpAnalytics.trackFrequencyModalClose)(currentSku);
    };

    const autoReplenishClick = isAutoReplenish => {
        setAutoReplenishChecked(isAutoReplenish);
        setIsAutoReplenMostCommon(isAutoReplenish ? checkIsAutoReplenMostCommon() : false);
    };

    const toggleDeliveryFrequencyModal = isOpen => {
        setIsFrequencyModalOpen(isOpen);
        fireDeliveryFrequencyModalTracking(isOpen);
    };

    const updateFrequencyModal = ({
        replenishmentFreqNum: freqNum = replenishmentFreqNum,
        replenishmentFreqType: freqType = replenishmentFreqType,
        quantity: qty
    }) => {
        setIsFrequencyModalOpen(false);
        setReplenishmentFreqNum(freqNum);
        setReplenishmentFreqType(freqType);
        setQuantity(qty);
    };

    const showQuantityWarning = () => {
        dispatch(
            Actions.showInfoModal({
                isOpen: true,
                title: getText('quantityWarningTitle'),
                message: getText('quantityWarningMessage'),
                buttonText: getText('quantityWarningButtonText'),
                callback: () => setQuantity(1),
                showCloseButton: true
            })
        );
    };

    const replenishMessage = sku => {
        const { isAutoReplenishmentEnabled } = Sephora.configurationSettings;
        const isFrench = localeUtils.isFrench();
        const autoReplenPromoText = isFrench ? 'avec l’aubaine autoprovision*' : 'with Auto-Replenish Hot Deal*';

        if (!isAutoReplenishmentEnabled) {
            return null;
        }

        return (
            <Text
                is='span'
                css={{
                    textTransform: 'capitalize',
                    marginTop: 0,
                    [mediaQueries.xsMax]: { marginTop: '5px' }
                }}
                fontSize='sm'
            >
                {getAutoReplenText('getItFor')}{' '}
                <Text
                    is='span'
                    color='red'
                    fontWeight='bold'
                    children={`${formatSavingAmountString(sku)} `}
                />
                {sku?.acceleratedPromotion ? autoReplenPromoText : getAutoReplenText('withAutoReplen')}
            </Text>
        );
    };

    useEffect(() => {
        const skuChanged = prevSkuIdRef.current !== currentSku?.skuId;
        const freqNumChanged = prevSkuIdRef.current && currentSku?.replenishmentFreqNum !== undefined;
        const freqTypeChanged = prevSkuIdRef.current && currentSku?.replenishmentFreqType !== undefined;
        const productChanged = prevProductIdRef.current !== currentProduct?.productDetails?.productId;

        if (skuChanged || freqNumChanged || freqTypeChanged) {
            setReplenishmentFreqNum(currentSku?.replenishmentFreqNum || '');
            setReplenishmentFreqType(currentSku?.replenishmentFreqType || '');

            if (skuChanged) {
                setQuantity(1);
                setAutoReplenishChecked(!productChanged && currentSku?.isReplenishmentEligible ? autoReplenishChecked : false);
            }

            setIsAutoReplenMostCommon(autoReplenishChecked ? checkIsAutoReplenMostCommon() : false);
        }

        prevSkuIdRef.current = currentSku?.skuId;
        prevProductIdRef.current = currentProduct?.productDetails?.productId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSku?.skuId, currentSku?.replenishmentFreqNum, currentSku?.replenishmentFreqType]);

    useEffect(() => {
        if (autoReplenishChecked && !prevAutoReplenishRef.current && quantity >= 2 && !isChangeQuantityEnabled()) {
            showQuantityWarning();
        }

        prevAutoReplenishRef.current = autoReplenishChecked;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoReplenishChecked]);

    return {
        quantity,
        setQuantity,
        autoReplenishChecked,
        replenishmentFreqNum,
        replenishmentFreqType,
        isFrequencyModalOpen,
        isAutoReplenMostCommon,
        autoReplenishClick,
        toggleDeliveryFrequencyModal,
        updateFrequencyModal,
        showQuantityWarning,
        replenishMessage
    };
};

export default useCommerce;
